import { HOW_TO_US_CITIES_FILTER_BULK_DIALOG_VIDEO_URL } from '@aibees/elephant/constants';
import { SearchMatchType, SearchType } from '@aibees/elephant/types';
import { Icon } from '@aibees/shared/components';
import { Alert, Box, Button, Typography } from '@mui/material';
import {
  getButtonGroupFilterTags,
  getLocationFilterFieldTags,
  locationsLabelFormatter,
  zipCodeLabelParser,
  zipCodeValueFormatter,
} from '../components/filter-tags/utils';
import {
  FieldValidationType,
  FilterCategory,
  LocatioData,
  SearchFilter,
  SearchFilterBase,
  SearchFilterType,
} from '../types';
import { locationsApiResponseMapper } from '../utils';
import { getLocationTreeOptions } from './locations';

export default function locationFilterConfig(
  searchType: SearchType,
  employees?: boolean
): SearchFilterBase {
  const isCompanySearch = searchType === SearchType.Company;
  const companiesLocation: SearchFilter = {
    name: isCompanySearch ? '' : 'Company HQ',
    type: isCompanySearch ? SearchFilterType.MIXED : SearchFilterType.NONE,
    icon: 'office-building-location',
    index: 0,
    key: 'company_hq',
    field: 'company_hq',
    metadata: {
      tabs: {
        icon: 'office-building-location',
        iconColor: 'primary',
      },
    },
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: isCompanySearch ? 'Company Location' : 'Company HQ',
        },
      },
    },
    open: true,
    mainItem: true,
    items: [
      {
        name: 'Include World Regions / E-Zones',
        type: SearchFilterType.CUSTOM_SELECT_TREE,
        icon: undefined,
        index: 0,
        layer: 4,
        key: 'company_hq_include_location_region',
        field: 'company_hq_include_location_region',
        nested: true,
        metadata: {
          color: 'primary',
          delimiter: '::',
          placeholder: 'Enter Location, (OR) Search',
          autocomplete: {
            customOptions: true,
            multiple: true,
            checkboxPosition: 'right',
            searchTriggerMinLength: 2,
          },
        },
        tabs: ['Continents', 'Economic Zones'],
        treeOptions: getLocationTreeOptions(),
        utils: {
          tags: {
            getFilterItemTagFunc: getLocationFilterFieldTags,
            labelFormatter: locationsLabelFormatter,
          },
        },
      },
      {
        name: 'Exclude World Regions / E-Zones',
        type: SearchFilterType.CUSTOM_SELECT_TREE,
        icon: undefined,
        index: 0,
        layer: 4,
        key: 'company_hq_exclude_location_region',
        field: 'company_hq_exclude_location_region',
        nested: true,
        metadata: {
          color: 'error',
          icon: 'block',
          delimiter: '::',
          placeholder: 'Enter Location, (OR) Search',
          autocomplete: {
            customOptions: true,
            multiple: true,
            checkboxPosition: 'right',
            searchTriggerMinLength: 2,
          },
        },
        tabs: ['Continents', 'Economic Zones'],
        treeOptions: getLocationTreeOptions(),
        utils: {
          tags: {
            getFilterItemTagFunc: getLocationFilterFieldTags,
            labelFormatter: locationsLabelFormatter,
          },
        },
      },
      {
        name: 'Cities',
        type: SearchFilterType.ACCORDION_SECTION,
        icon: 'share-location',
        index: 0,
        layer: 3,
        key: 'company_hq_cities',
        field: 'company_hq_cities',
        metadata: { color: 'primary' },
        nested: true,
        open: true,
        items: [
          {
            name: 'Include Cities',
            type: SearchFilterType.CUSTOM_SELECT,
            index: 0,
            exactMatch: {
              enabled: true,
              default: SearchMatchType.SMART,
            },
            field: 'company_hq_include_location_cities',
            key: 'company_hq_include_location_cities',
            layer: 4,
            metadata: {
              color: 'primary',
              icon: 'add-circle-outline',
              preset: 'cities',
              placeholder: 'Enter Location, (OR) Search',
              autocomplete: {
                recent: false,
                checkboxPosition: 'right',
                multiple: true,
                searchTriggerMinLength: 2,
                allowCustomValuesByEnter: true,
              },
              advancedButtonVisible: true,
              dialog: {
                title: 'Bulk Include Cities',
                subTitleItemLabel: 'Cities',
                subTitleItemLabelPlural: 'Cities',
                icon: 'share-location',
                textArea: {
                  placeholder:
                    'Hamburg Germany, Paris France, Milan Italy, Barcelona Spain',
                },
                preTextAreaComponent: preTextAreaComponentForBulkCitiesDialog(),
                postTextAreaComponent:
                  postTextAreaComponentForBulkCitiesDialog(),
                customLeftActions: customLeftActionsForBulkCitiesDialog(),
              },
            },
            utils: {
              optionsApiFormatter: (data?: LocatioData[]) =>
                locationsApiResponseMapper(data),
            },
            searchKey: 'search',
            config: { labelKey: 'value', valueKey: 'value' },
            options: [
              {
                type: 'URL',
                value: `/elephant-management/um/locations?size=25&page=0`,
                label: '',
                icon: null,
                index: undefined,
              },
            ],
          },
          {
            name: 'Exclude Cities',
            type: SearchFilterType.CUSTOM_SELECT,
            index: 0,
            exactMatch: {
              enabled: true,
              default: SearchMatchType.SMART,
            },
            field: 'company_hq_exclude_location_cities',
            key: 'company_hq_exclude_location_cities',
            layer: 4,
            metadata: {
              color: 'error',
              icon: 'block',
              preset: 'cities',
              placeholder: 'Enter Location, (OR) Search',
              autocomplete: {
                recent: false,
                checkboxPosition: 'right',
                multiple: true,
                searchTriggerMinLength: 2,
                allowCustomValuesByEnter: true,
              },
              advancedButtonVisible: true,
              dialog: {
                title: 'Bulk Exclude Cities',
                subTitleItemLabel: 'Cities',
                subTitleItemLabelPlural: 'Cities',
                icon: 'share-location',
                textArea: {
                  placeholder:
                    'Hamburg Germany, Paris France, Milan Italy, Barcelona Spain',
                },
                preTextAreaComponent: preTextAreaComponentForBulkCitiesDialog(),
                postTextAreaComponent:
                  postTextAreaComponentForBulkCitiesDialog(),
                customLeftActions: customLeftActionsForBulkCitiesDialog(),
              },
            },
            utils: {
              optionsApiFormatter: (data?: LocatioData[]) =>
                locationsApiResponseMapper(data),
            },
            searchKey: 'search',
            config: { labelKey: 'value', valueKey: 'value' },
            options: [
              {
                type: 'URL',
                value: `/elephant-management/um/locations?size=25&page=0`,
                label: '',
                icon: null,
                index: undefined,
              },
            ],
          },
        ],
      },
      {
        name: 'Zip Code Radius',
        type: SearchFilterType.NONE,
        icon: undefined,
        index: 1,
        key: 'company_hq_zip_code',
        field: 'company_hq_zip_code',
        metadata: { color: 'primary' },
        config: { labelKey: 'label', valueKey: 'value' },
        nested: true,
        comingSoon: true,
        disabled: true,
        open: false,
        items: [
          {
            name: '',
            type: SearchFilterType.MIXED,
            index: 0,
            key: 'company_hq_include_zip_code',
            field: 'company_hq_include_zip_code',
            metadata: { color: 'primary' },
            nested: true,
            items: [
              {
                name: 'Include',
                field: '',
                key: '',
                index: 0,
                type: SearchFilterType.TITLE,
                metadata: { color: 'primary' },
              },
              {
                name: 'Include Zip Code',
                type: SearchFilterType.TEXT_FIELD,
                icon: undefined,
                index: 0,
                key: 'company_hq_include_zip_code_country',
                field: 'company_hq_include_zip_code_country',
                metadata: {
                  color: 'primary',
                  placeholder: 'Enter Zip Code / City Name',
                  styles: {
                    sx: {
                      padding: 0,
                      border: 0,
                      ':hover': {
                        border: 0,
                      },
                    },
                  },
                  // preset: 'company_hq_zipcode',
                  autocomplete: {
                    recent: false,
                    dontFilterOptions: true,
                  },
                  delimiter: ':',
                  singleEntry: true,
                },
                config: { labelKey: 'name', valueKey: 'location' },
                nested: true,
                searchKey: 'search',
                utils: {
                  valueFormatter: zipCodeValueFormatter,
                  tags: {
                    labelFormatter: zipCodeLabelParser,
                  },
                },
                options: [
                  {
                    type: 'URL',
                    value:
                      '/elephant-management/um/workspaces/:wid/locations?page=0&size=10',
                    label: '',
                    icon: null,
                    index: undefined,
                  },
                ],
              },
              {
                name: 'Raduis',
                field: '',
                key: '',
                index: 0,
                type: SearchFilterType.TITLE,
                metadata: { color: 'primary' },
              },
              {
                name: 'Include Zip Code Distance',
                type: SearchFilterType.TEXT_FIELD,
                icon: undefined,
                index: 2,
                key: 'company_hq_include_zip_code_distance',
                field: 'company_hq_include_zip_code_distance',
                metadata: {
                  color: 'primary',
                  placeholder: 'Enter Radius',
                  validation: {
                    type: FieldValidationType.NUMBER,
                    error: 'Please enter a numeric value',
                  },
                  singleEntry: true,
                },
                nested: true,
              },
              {
                name: 'Unit',
                field: '',
                key: '',
                index: 0,
                type: SearchFilterType.TITLE,
                metadata: { color: 'primary' },
              },
              {
                type: SearchFilterType.RADIO,
                icon: undefined,
                index: 0,
                field: 'company_hq_include_zip_code_unit',
                key: 'company_hq_include_zip_code_unit',
                name: 'Distance Unit',
                metadata: {
                  color: 'primary',
                  skipUrlParams: true,
                },
                options: [
                  {
                    type: 'STRING',
                    value: 'Miles',
                    label: 'Miles',
                    icon: null,
                    index: 1,
                    selected: true,
                  },
                  {
                    type: 'STRING',
                    value: 'Kilometers',
                    label: 'Kilometers',
                    icon: null,
                    index: 2,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };
  const peopleLocation: SearchFilter = {
    name: 'People',
    type: SearchFilterType.NONE,
    index: 0,
    key: 'location',
    field: 'location',
    metadata: {
      tabs: {
        icon: 'people',
        iconColor: 'success',
      },
    },
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'Contacts',
        },
      },
    },
    open: true,
    nested: true,
    mainItem: true,
    items: [
      {
        name: 'Include World Regions / E-Zones',
        type: SearchFilterType.CUSTOM_SELECT_TREE,
        icon: undefined,
        index: 0,
        layer: 4,
        key: 'contact_include_location_region',
        field: 'contact_include_location_region',
        nested: true,
        metadata: {
          color: 'primary',
          delimiter: '::',
          placeholder: 'Enter Location, (OR) Search',
          autocomplete: {
            customOptions: true,
            multiple: true,
            checkboxPosition: 'right',
            searchTriggerMinLength: 2,
          },
        },
        tabs: ['Continents', 'Economic Zones'],
        treeOptions: getLocationTreeOptions(),
        utils: {
          tags: {
            getFilterItemTagFunc: getLocationFilterFieldTags,
            labelFormatter: locationsLabelFormatter,
          },
        },
      },
      {
        name: 'Exclude World Regions / E-Zones',
        type: SearchFilterType.CUSTOM_SELECT_TREE,
        icon: undefined,
        index: 0,
        layer: 4,
        key: 'contact_exclude_location_region',
        field: 'contact_exclude_location_region',
        nested: true,
        metadata: {
          color: 'error',
          icon: 'block',
          delimiter: '::',
          placeholder: 'Enter Location, (OR) Search',
          autocomplete: {
            customOptions: true,
            multiple: true,
            checkboxPosition: 'right',
            searchTriggerMinLength: 2,
          },
        },
        tabs: ['Continents', 'Economic Zones'],
        treeOptions: getLocationTreeOptions(),
        utils: {
          tags: {
            getFilterItemTagFunc: getLocationFilterFieldTags,
            labelFormatter: locationsLabelFormatter,
          },
        },
      },
      {
        name: 'Cities',
        type: SearchFilterType.ACCORDION_SECTION,
        icon: 'share-location',
        index: 0,
        layer: 3,
        key: 'contact_cities',
        field: 'contact_cities',
        metadata: { color: 'primary' },
        nested: true,
        open: true,
        items: [
          {
            name: 'Include Cities',
            type: SearchFilterType.CUSTOM_SELECT,
            index: 0,
            exactMatch: {
              enabled: true,
              default: SearchMatchType.SMART,
            },
            field: 'contact_include_location_cities',
            key: 'contact_include_location_cities',
            layer: 4,
            metadata: {
              color: 'primary',
              icon: 'add-circle-outline',
              preset: 'cities',
              placeholder: 'Enter Location, (OR) Search',
              autocomplete: {
                recent: false,
                checkboxPosition: 'right',
                multiple: true,
                searchTriggerMinLength: 2,
                allowCustomValuesByEnter: true,
              },
              advancedButtonVisible: true,
              dialog: {
                title: 'Bulk Include Cities',
                subTitleItemLabel: 'Cities',
                subTitleItemLabelPlural: 'Cities',
                icon: 'share-location',
                textArea: {
                  placeholder:
                    'Hamburg Germany, Paris France, Milan Italy, Barcelona Spain',
                },
                preTextAreaComponent: preTextAreaComponentForBulkCitiesDialog(),
                postTextAreaComponent:
                  postTextAreaComponentForBulkCitiesDialog(),
                customLeftActions: customLeftActionsForBulkCitiesDialog(),
              },
            },
            utils: {
              optionsApiFormatter: (data?: LocatioData[]) =>
                locationsApiResponseMapper(data),
            },
            searchKey: 'search',
            config: { labelKey: 'value', valueKey: 'value' },
            options: [
              {
                type: 'URL',
                value: `/elephant-management/um/locations?size=25&page=0`,
                label: '',
                icon: null,
                index: undefined,
              },
            ],
          },
          {
            name: 'Exclude Cities',
            type: SearchFilterType.CUSTOM_SELECT,
            index: 0,
            exactMatch: {
              enabled: true,
              default: SearchMatchType.SMART,
            },
            field: 'contact_exclude_location_cities',
            key: 'contact_exclude_location_cities',
            layer: 4,
            metadata: {
              color: 'error',
              icon: 'block',
              preset: 'cities',
              placeholder: 'Enter Location, (OR) Search',
              autocomplete: {
                recent: false,
                checkboxPosition: 'right',
                multiple: true,
                searchTriggerMinLength: 2,
                allowCustomValuesByEnter: true,
              },
              advancedButtonVisible: true,
              dialog: {
                title: 'Bulk Exclude Cities',
                subTitleItemLabel: 'Cities',
                subTitleItemLabelPlural: 'Cities',
                icon: 'share-location',
                textArea: {
                  placeholder:
                    'Hamburg Germany, Paris France, Milan Italy, Barcelona Spain',
                },
                preTextAreaComponent: preTextAreaComponentForBulkCitiesDialog(),
                postTextAreaComponent:
                  postTextAreaComponentForBulkCitiesDialog(),
                customLeftActions: customLeftActionsForBulkCitiesDialog(),
              },
            },
            utils: {
              optionsApiFormatter: (data?: LocatioData[]) =>
                locationsApiResponseMapper(data),
            },
            searchKey: 'search',
            config: { labelKey: 'value', valueKey: 'value' },
            options: [
              {
                type: 'URL',
                value: `/elephant-management/um/locations?size=25&page=0`,
                label: '',
                icon: null,
                index: undefined,
              },
            ],
          },
        ],
      },
      {
        name: 'Zip Code Radius',
        type: SearchFilterType.NONE,
        icon: undefined,
        index: 1,
        key: 'location_zip_code',
        field: 'location_zip_code',
        metadata: { color: 'primary' },
        config: { labelKey: 'label', valueKey: 'value' },
        nested: true,
        comingSoon: true,
        disabled: true,
        open: false,
        items: [
          {
            name: '',
            type: SearchFilterType.MIXED,
            index: 0,
            key: 'contact_include_zip_code',
            field: 'contact_include_zip_code',
            metadata: { color: 'primary' },
            nested: true,
            items: [
              {
                name: 'Include',
                field: '',
                key: '',
                index: 0,
                type: SearchFilterType.TITLE,
                metadata: { color: 'primary' },
              },
              {
                name: 'Include Zip Code',
                type: SearchFilterType.TEXT_FIELD,
                icon: undefined,
                index: 0,
                key: 'contact_include_zip_code_country',
                field: 'contact_include_zip_code_country',
                metadata: {
                  color: 'primary',
                  placeholder: 'Enter Zip Code / City Name',
                  styles: {
                    sx: {
                      padding: 0,
                      border: 0,
                      ':hover': {
                        border: 0,
                      },
                    },
                  },
                  // preset: 'company_hq_zipcode',
                  autocomplete: {
                    recent: false,
                    dontFilterOptions: true,
                  },
                  delimiter: ':',
                  singleEntry: true,
                },
                config: { labelKey: 'name', valueKey: 'location' },
                nested: true,
                searchKey: 'search',
                utils: {
                  valueFormatter: zipCodeValueFormatter,
                  tags: {
                    labelFormatter: zipCodeLabelParser,
                  },
                },
                options: [
                  {
                    type: 'URL',
                    value:
                      '/elephant-management/um/workspaces/:wid/locations?page=0&size=10',
                    label: '',
                    icon: null,
                    index: undefined,
                  },
                ],
              },
              {
                name: 'Raduis',
                field: '',
                key: '',
                index: 0,
                type: SearchFilterType.TITLE,
                metadata: { color: 'primary' },
              },
              {
                name: 'Include Zip Code Distance',
                type: SearchFilterType.TEXT_FIELD,
                icon: undefined,
                index: 2,
                key: 'contact_include_zip_code_distance',
                field: 'contact_include_zip_code_distance',
                metadata: {
                  color: 'primary',
                  placeholder: 'Enter Radius',
                  validation: {
                    type: FieldValidationType.NUMBER,
                    error: 'Please enter a numeric value',
                  },
                  singleEntry: true,
                },
                nested: true,
              },
              {
                name: 'Unit',
                field: '',
                key: '',
                index: 0,
                type: SearchFilterType.TITLE,
                metadata: { color: 'primary' },
              },
              {
                type: SearchFilterType.RADIO,
                icon: undefined,
                index: 0,
                field: 'contact_include_zip_code_unit',
                key: 'contact_include_zip_code_unit',
                name: 'Distance Unit',
                metadata: {
                  color: 'primary',
                  skipUrlParams: true,
                },
                options: [
                  {
                    type: 'STRING',
                    value: 'Miles',
                    label: 'Miles',
                    icon: null,
                    index: 1,
                    selected: true,
                  },
                  {
                    type: 'STRING',
                    value: 'Kilometers',
                    label: 'Kilometers',
                    icon: null,
                    index: 2,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };

  return {
    name: 'Location',
    icon: 'location',
    index: 24,
    type: SearchFilterType.ROOT,
    category: isCompanySearch
      ? [FilterCategory.COMPANY_DATA]
      : [FilterCategory.COMPANY_DATA, FilterCategory.CONTACT_DATA],
    pinned: true,
    utils: {
      tags: isCompanySearch
        ? {}
        : {
            function: getButtonGroupFilterTags,
          },
    },
    items: isCompanySearch
      ? [companiesLocation]
      : employees
      ? [peopleLocation]
      : [
          {
            name: '',
            type: SearchFilterType.BUTTON_GROUP,
            icon: undefined,
            index: 0,
            key: '',
            field: '',
            metadata: {
              allowParallelItems: true,
              tabs: {
                defaultOpenIndex: 1,
              },
            },
            config: { labelKey: 'label', valueKey: 'value' },
            items: [
              {
                name: 'Applied Filters',
                key: 'location_applied_filters',
                field: 'location_applied_filters',
                type: SearchFilterType.APPLIED_FILTERS,
                index: 2,
                icon: undefined,
                metadata: {},
              },
              companiesLocation,
              peopleLocation,
            ],
          },
        ],
  };
}

const preTextAreaComponentForBulkCitiesDialog = () => {
  return (
    <Alert severity="info" icon={<Icon name="info" size={20} />}>
      <Box display="flex" flexDirection="column" gap={0.5}>
        <Typography variant="body3" fontWeight={700}>
          Always write the country right after the city - no comma between them.
        </Typography>
        <Typography variant="body3">
          Use a comma only to separate entries. <b>“Hamburg”</b> alone may match
          Hamburg, USA - write <b>“Hamburg Germany”</b> to get the right result.
        </Typography>
      </Box>
    </Alert>
  );
};

const postTextAreaComponentForBulkCitiesDialog = () => {
  return (
    <Alert
      severity="default"
      icon={<Icon name="help-center-outline" size={20} />}
    >
      <Box display="flex" flexDirection="column" gap={0.5}>
        <Typography variant="body3" fontWeight={700}>
          Have a long list?
        </Typography>
        <Typography variant="body3">
          Paste your cities into ChatGPT, Gemini, or Cursor and ask:
          <br />
          <b>
            ”Format as: City Country (no comma between city and country),
            separated by commas. Use full country names only (no abbreviations
            like USA, UK, UAE). Always use sovereign country names (e.g.,
            Scotland → United Kingdom). Keep order.”
          </b>
        </Typography>
      </Box>
    </Alert>
  );
};

const customLeftActionsForBulkCitiesDialog = () => {
  const handleRedirect = () => {
    window.open(
      HOW_TO_US_CITIES_FILTER_BULK_DIALOG_VIDEO_URL,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <Button
      variant="outlined"
      color="default"
      size="small"
      startIcon={<Icon name="play-circle-outline" />}
      onClick={handleRedirect}
    >
      Learn how to add cities correctly
    </Button>
  );
};
